import React, { useState, forwardRef, useImperativeHandle, useRef, useEffect } from 'react'

import RepliesList from '../../../containers/dynamic/form/replies/RepliesList'
import Separator from '../../../packages/kiti/components/helpers/Separator'
import ReplyWarnTags from '../../../containers/dynamic/form/replies/ReplyWarnTags'
import ReplySubdataControl from '../../../containers/dynamic/form/replies/ReplySubdataControl'
import NAWrapper from '../NAWrapper'
import useLogicReplies from './hooks/useLogicReplies'
import useCalcReplies from './hooks/useCalcReplies'
import useIgnoreValidation from './hooks/useIgnoreValidation'
import { checkReplyValidation } from '@btime/helpers'
import { Absolute } from '../../../packages/kiti/components/helpers/Position'
import { FlexCol } from '../../structure/FlexGrid'

const QUESTIONS_THAT_USE_ON_STOP_TYPING = [
  'calc',
  'integer',
  'numeric',
  'number',
  'float',
  'email',
  'phoneNumber',
  'cpf_cnpj',
  'text'
]

const ImperativeWrapper = forwardRef(
  (
    {
      children,
      questionId,
      value,
      questionReference,
      questionType,
      replies,
      reply,
      allReplies,
      components,
      onChange,
      allowNull,
      ...props
    },
    ref
  ) => {
    const [localReply, setLocalReply] = useState(reply)
    const localRef = useRef()

    const replyValueKey = reply?.value?.documentId ?? reply?.value?.src ?? ''
    useEffect(() => {
      if (!replyValueKey) return
      setLocalReply(prev => {
        const prevKey = prev?.value?.documentId ?? prev?.value?.src ?? ''
        if (replyValueKey && !prevKey) return { ...reply }
        return prev
      })
    }, [replyValueKey])

    const logicReplies = useLogicReplies(allReplies, localReply, { questionType, questionReference })
    const ignoreValidation = useIgnoreValidation(localReply, setLocalReply)
    useCalcReplies(localReply, components, allReplies, { questionType })

    const handleChange = value => {
      setLocalReply(prev => ({ ...prev, ...value }))
      if (onChange) {
        onChange(value)
      }
    }

    const renderChildren = () => {
      if (!children) return
      const common = {
        value: localReply?.value,
        localReply,
        onFocus: props.onFocus
      }
      if (QUESTIONS_THAT_USE_ON_STOP_TYPING.includes(questionType)) {
        return React.cloneElement(children, {
          ...common,
          onStopTyping: newValue => handleChange({ value: newValue })
        })
      }
      return React.cloneElement(children, {
        ...common,
        onChange: newValue => handleChange({ value: newValue })
      })
    }

    useImperativeHandle(
      ref,
      () => {
        return {
          reset: () => {
            setLocalReply(prev => ({ ...prev, value: undefined }))
          },
          setFocus: () => {
            localRef?.current.scrollIntoView({ behavior: 'smooth', block: 'start', inline: 'nearest' })
          },
          setValue: newValue => {
            setLocalReply(prev => {
              const next = { ...prev, value: newValue }
              return { ...next, validations: checkReplyValidation(next) }
            })
          },
          getValue: () => {
            return localReply?.value
          },
          getReply: () => {
            return localReply
          }
        }
      },
      [localReply?.value, localReply?.validations, localReply?.documents, localReply?.notes]
    )

    return (
      <>
        <Absolute top={-2}>
          <div ref={localRef}></div>
        </Absolute>
        <Absolute top={1} right={1}>
          {!ignoreValidation && (
            <FlexCol widthOnSm="100%">
              <ReplyWarnTags reply={localReply} />
            </FlexCol>
          )}
        </Absolute>
        <NAWrapper
          onChange={value => handleChange({ value })}
          value={localReply?.value}
          active={allowNull && !['checkbox', 'select'].includes(questionType)}
        >
          <>{renderChildren()}</>
        </NAWrapper>
        <ReplySubdataControl reply={localReply} handleChange={handleChange} />

        <Separator weight={5} />
        {!!logicReplies.length && (
          <RepliesList replies={logicReplies} components={components} allReplies={allReplies} isChild {...props} />
        )}
      </>
    )
  },
  []
)

export default ImperativeWrapper
